import { useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { PROFILE_INPUT_TYPES } from 'src/apis/dto/DTOProfileInputPopups';
import InterestsSelectionModal from 'src/components/InterestsSelectionModal';
import ProfileInputIntroModal from 'src/components/ProfileInputModals/ProfileInputIntroModal';
import useEnableRemoteConfig from 'src/hooks/useEnableRemoteConfig';
import { useReportPopupViewedMutation } from 'src/hooks/usePopupQuery';
import { useProfileInputPopupsQuery } from 'src/hooks/usePopupQuery';
import { remoteConfigAtom } from 'src/stores/auth/atoms';
import { ModalType } from 'src/types/Modal';
import { groupInterestsByCategory } from 'src/utils/interest';
import { OrderedModalHookType } from './types';
import withLoginUserModalPromise from './withLoginUserModalPromise';
const DEFAULT_MIN_COUNT = 3;
const DEFAULT_MAX_COUNT = 30;
const useProfileInputInterestsModal: OrderedModalHookType = modalPropsPromise => {
  const remoteConfig = useAtomValue(remoteConfigAtom);
  const showProfileInputPopup = Boolean(useEnableRemoteConfig('showProfileInputPopup'));
  const minCount = remoteConfig?.interestsSelectionMinCount ?? DEFAULT_MIN_COUNT;
  const maxCount = remoteConfig?.interestsSelectionMaxCount ?? DEFAULT_MAX_COUNT;
  const {
    data: popupsData,
    isFetching
  } = useProfileInputPopupsQuery({
    enabled: showProfileInputPopup
  });
  const interestPopup = useMemo(() => {
    const popups = popupsData?.data?.result?.popups;
    if (!popups) return null;
    return popups.find(popup => popup.inputType === PROFILE_INPUT_TYPES.INTEREST) ?? null;
  }, [popupsData]);
  const categorizedInterests = useMemo(() => interestPopup?.interestSelection ? groupInterestsByCategory(interestPopup.interestSelection) : [], [interestPopup]);
  const {
    mutate: submitViewed
  } = useReportPopupViewedMutation();
  useEffect(() => {
    if (!modalPropsPromise) return;

    // remote config가 꺼져 있으면 팝업 비노출
    if (!showProfileInputPopup) {
      modalPropsPromise.resolve(null);
      return;
    }

    // 쿼리가 아직 진행 중이면 대기 (성공/실패 모두 isFetching이 false가 됨)
    if (isFetching) return;

    // INTEREST 타입 팝업이 없거나 API 실패 시 비노출
    if (!interestPopup || categorizedInterests.length === 0) {
      modalPropsPromise.resolve(null);
      return;
    }
    const {
      popupId,
      inputType
    } = interestPopup;
    modalPropsPromise.resolve([{
      modalProps: {
        key: ModalType.PROFILE_INPUT_INTRO,
        component: ProfileInputIntroModal
      }
    }, {
      modalProps: {
        key: ModalType.INTERESTS_SELECTION,
        component: () => <InterestsSelectionModal trigger='PROFILE_INPUT' categorizedInterests={categorizedInterests} minCount={minCount} maxCount={maxCount} onDisplay={() => submitViewed({
          popupType: inputType,
          popupId
        })} />
      }
    }]);
  }, [modalPropsPromise, showProfileInputPopup, isFetching, interestPopup, categorizedInterests, minCount, maxCount, submitViewed]);
};
export default withLoginUserModalPromise(useProfileInputInterestsModal);